// ---------------------------------------------------------------------------
// PlanogramProductCellFactory
//
// Converts ProductOption objects into planogram cells.
//
// Weight/Volume behavior:
//
// - The product provides the default numeric value and unit.
// - The slot numeric value can be changed manually.
// - A manually changed slot value has priority over the product value.
// - The product provides the initial unit.
// - The slot unit can be changed manually.
// ---------------------------------------------------------------------------

import {
    normalizeWeightOrVolume,
} from '../../model/PlanogramProductModel';

import {
    PlanogramCellData,
    ProductOption,
} from './types';

const safeString = (
    value: unknown,
): string =>
    value === null ||
    value === undefined
        ? ''
        : String(value);

const parseNumericValue = (
    value: unknown,
): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value)
            ? value
            : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalized =
        value.trim().replace(',', '.');

    if (!normalized) {
        return null;
    }

    const parsed = Number(normalized);

    return Number.isFinite(parsed)
        ? parsed
        : null;
};

const getStructuredWeightOrVolume = (
    product:
        | ProductOption
        | Record<string, unknown>,
): Record<string, unknown> | null => {
    const structuredValue =
        product?.weightOrVolume ??
        product?.weight_or_volume ??
        null;

    return structuredValue &&
        typeof structuredValue === 'object'
        ? structuredValue as Record<
            string,
            unknown
        >
        : null;
};

const getProductWeightOrVolume = (
    product:
        | ProductOption
        | Record<string, unknown>,
) =>
    normalizeWeightOrVolume(
        product?.weightOrVolume ??
            product?.weight_or_volume,
        product?.weightVolume ??
            product?.weight_volume,
        product?.unitOfMeasurement ??
            product?.unit_of_measurement,
    );

const getUnitValue = (
    value: unknown,
): string | null => {
    if (
        value === null ||
        value === undefined
    ) {
        return null;
    }

    if (
        typeof value === 'string' ||
        typeof value === 'number'
    ) {
        return String(value).trim() ||
            null;
    }

    if (typeof value === 'object') {
        const option =
            value as Record<
                string,
                unknown
            >;

        const localizedName =
            option.name &&
            typeof option.name === 'object'
                ? option.name as Record<
                    string,
                    unknown
                >
                : null;

        const unit = safeString(
            option.value ??
            option.id ??
            option.code ??
            localizedName?.name ??
            localizedName?.namePl ??
            option.name ??
            option.label ??
            option.parsedValue ??
            option.source,
        ).trim();

        return unit || null;
    }

    return null;
};

const getProductUnit = (
    product:
        | ProductOption
        | Record<string, unknown>,
): string | null => {
    const structuredValue =
        getStructuredWeightOrVolume(
            product,
        );

    const rawUnit =
        structuredValue?.unit ??
        product?.unitOfMeasurement ??
        product?.unit_of_measurement ??
        null;

    return getUnitValue(rawUnit);
};

const getProductDefaultValue = (
    product:
        | ProductOption
        | Record<string, unknown>,
): number | null => {
    const structuredValue =
        getStructuredWeightOrVolume(
            product,
        );

    return parseNumericValue(
        structuredValue?.value ??
        product?.weightVolume ??
        product?.weight_volume,
    );
};

export class PlanogramProductCellFactory {
    /**
    );
};

export class PlanogramProductCellFactory {
        * Performs the optimistic product write.
     *
     * The selected product supplies the initial Weight/Volume value and unit.
     * The numeric value can subsequently be changed on the planogram slot.
     */
    static build(
        currentCell: PlanogramCellData,
        productOption: ProductOption,
    ): PlanogramCellData {
        const productId =
            productOption.externalId ||
            productOption.id ||
            productOption.value ||
            productOption.systemId;

        const productName =
            safeString(
                productOption.name,
            ) ||
            safeString(
                productOption.label,
            ) ||
            safeString(
                productOption.productName,
            ) ||
            '';

        const productWeightOrVolume =
            getProductWeightOrVolume(
                productOption,
            );

        const weightVolume =
            productWeightOrVolume?.value ??
            getProductDefaultValue(
                productOption,
            );

        const unitOfMeasurement =
            getProductUnit(
                productOption,
            );

        const weightOrVolume =
            weightVolume !== null
                ? {
                    value:
                        weightVolume,
                    unit:
                        unitOfMeasurement,
                }
                : null;

        return {
            ...currentCell,

            type:
                'product',

            productName,

            productExternalId:
                productId,

            product: {
                id:
                    productId,
                value:
                    productId,
                externalId:
                    productOption
                        .externalId ??
                    productId,
                systemId:
                    productOption.systemId,
                externalSystemId:
                    productOption
                        .externalSystemId,
                name:
                    productName,
                label:
                    productName,
                productName,

                ean:
                    productOption.ean,

                price:
                    productOption.price ??
                    '',

                currency:
                    productOption.currency ??
                    null,

                categoryName:
                    productOption
                        .categoryName ??
                    null,

                categoryColor:
                    productOption
                        .categoryColor ??
                    null,

                photoUrl:
                    productOption.photoUrl ??
                    null,

                hasPhoto:
                    productOption.hasPhoto ??
                    false,

                weightOrVolume,
                weightVolume,
                unitOfMeasurement,
            },

            price:
                productOption.price ??
                '',

            currency:
                productOption.currency ??
                null,

            minQuantity:
                productOption.minQuantity ??
                0,

            maxQuantity:
                productOption.maxQuantity ??
                0,

            categoryColor:
                productOption.categoryColor ??
                null,

            categoryName:
                productOption.categoryName ??
                null,

            ean:
                productOption.ean ??
                null,

            weightOrVolume,
            weightVolume,
            unitOfMeasurement,

            hasPhoto:
                productOption.hasPhoto ??
                false,
        };
    }

    /**
     * Patches product details fetched after the optimistic selection.
     *
     * Precedence:
     *
     * - An existing slot Weight/Volume value wins over the product value.
     * - The product value is used when the slot has no value.
         * - An existing slot unit wins over the product unit.
     *
     * Product name/label/productName are intentionally not changed.
     */
    static patchDetails(
        currentCell: PlanogramCellData,
        fullProduct: ProductOption,
    ): PlanogramCellData {
        if (!currentCell.product) {
            return currentCell;
        }

        const currentProduct =
            currentCell.product;

        const currentStructuredValue =
            currentCell.weightOrVolume ??
            currentCell.product
                ?.weightOrVolume ??
            null;

        /*
         * A slot value may already be a manual override. It must not be
         * replaced by a later product-details request.
         */
        const currentSlotValue =
            parseNumericValue(
                currentStructuredValue
                    ?.value ??
                currentCell
                    .weightVolume ??
                currentProduct
                    .weightVolume,
            );

        /*
         * Used only when the slot does not yet contain a value.
         */
        const productDefaultValue =
            getProductDefaultValue(
                fullProduct,
            );

        const weightVolume =
            currentSlotValue ??
            productDefaultValue;

        /* Preserve a unit already stored on the slot. The details response
         * only supplies the default when the slot has no unit yet. */
        const unitOfMeasurement =
            getUnitValue(currentCell.unitOfMeasurement) ??
            getUnitValue(currentStructuredValue?.unit) ??
            getProductUnit(
                currentProduct,
            ) ??
            getProductUnit(fullProduct);

        /*
         * The API accepts a numeric value with an optional unit. Normally
         * the unit is populated from the product.
         */
        const weightOrVolume =
            weightVolume !== null
                ? {
                    value:
                        weightVolume,
                    unit:
                        unitOfMeasurement,
                }
                : null;

        const ean: string | number | null =
            fullProduct.ean ??
            currentCell.ean ??
            currentProduct.ean ??
            null;

        return {
            ...currentCell,

            price:
                fullProduct.price ??
                currentCell.price,

            minQuantity:
                fullProduct.minQuantity ??
                currentCell.minQuantity,

            maxQuantity:
                fullProduct.maxQuantity ??
                currentCell.maxQuantity,

            currency:
                fullProduct.currency ??
                currentCell.currency,

            ean,

            weightOrVolume,
            weightVolume,
            unitOfMeasurement,

            product: {
                ...currentProduct,

                price:
                    fullProduct.price ??
                    currentProduct.price,

                minQuantity:
                    fullProduct.minQuantity ??
                    currentProduct
                        .minQuantity,

                maxQuantity:
                    fullProduct.maxQuantity ??
                    currentProduct
                        .maxQuantity,

                currency:
                    fullProduct.currency ??
                    currentProduct.currency,

                ean,

                weightOrVolume,
                weightVolume,
                unitOfMeasurement,
            },
        };
    }
}
